/**
 * Provides portal preferences to the React tree. On mount, reads from
 * localStorage + applies to the DOM. Exposes a `setPreferences` updater
 * that also re-applies + persists.
 *
 * Listens for OS-level changes to prefers-color-scheme + prefers-reduced-
 * motion so 'system' theme users track the OS automatically.
 *
 * The hook + context live in `preferences-context.ts` so Fast Refresh
 * can keep this file component-only.
 */

import { useCallback, useEffect, useState } from "react";

import { PreferencesContext } from "@/components/preferences-context";
import {
  applyPreferences,
  DEFAULT_PREFERENCES,
  loadPreferences,
  savePreferences,
  type Preferences,
} from "@/lib/preferences";

export function PreferencesProvider(props: { children: React.ReactNode }): JSX.Element {
  const [preferences, setPrefsState] = useState<Preferences>(loadPreferences);

  useEffect(() => {
    applyPreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const darkQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const rerun = (): void => {
      applyPreferences(preferences);
    };
    darkQuery.addEventListener("change", rerun);
    motionQuery.addEventListener("change", rerun);
    return () => {
      darkQuery.removeEventListener("change", rerun);
      motionQuery.removeEventListener("change", rerun);
    };
  }, [preferences]);

  const setPreferences = useCallback((update: Partial<Preferences>) => {
    setPrefsState((prev) => {
      const next: Preferences = { ...prev, ...update };
      savePreferences(next);
      return next;
    });
  }, []);

  const resetPreferences = useCallback(() => {
    setPrefsState(DEFAULT_PREFERENCES);
    savePreferences(DEFAULT_PREFERENCES);
  }, []);

  return (
    <PreferencesContext.Provider value={{ preferences, setPreferences, resetPreferences }}>
      {props.children}
    </PreferencesContext.Provider>
  );
}
